import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '@/store/useStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ColorPicker from '@/components/ui/ColorPicker';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Pencil } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getTeamById } from '@/services/playerService';
import { toast } from '@/components/ui/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Loader2 } from 'lucide-react';

// Helper functions for image manipulation
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Needed for cross-origin images
    image.src = url;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new size of a rotated rectangle.
 */
function rotateSize(
  width: number,
  height: number,
  rotation: number
): { width: number; height: number } {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

const NewTeamPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addTeam, tournaments, fetchTournaments } = useStore();
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [color, setColor] = useState('#10b981');
  const [tournamentId, setTournamentId] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Cropping states
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false); // State for crop dialog

  const [existingTeamInput, setExistingTeamInput] = useState('');
  const [isAddingExisting, setIsAddingExisting] = useState(false);
  const [previewTeams, setPreviewTeams] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    console.log("logoPreviewUrl changed:", logoPreviewUrl);
  }, [logoPreviewUrl]);

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        await fetchTournaments();
      } catch (error) {
        console.error('Error loading tournaments:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [fetchTournaments]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        setIsCropDialogOpen(true); // Open dialog on file selection
      }, false);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setImageSrc(null);
      setLogoPreviewUrl(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setIsCropDialogOpen(false);
    }
  };

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImage = async (
    imageSrc: string,
    pixelCrop: Area,
    rotation = 0,
    flip = { horizontal: false, vertical: false }
  ): Promise<File | null> => {
    const image = await createImage(imageSrc);

    const rotCanvas = document.createElement('canvas');
    const rotCtx = rotCanvas.getContext('2d');
    if (!rotCtx) return null;

    const rotRad = getRadianAngle(rotation);

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.naturalWidth,
      image.naturalHeight,
      rotation
    );

    // set canvas size to match the bounding box
    rotCanvas.width = bBoxWidth;
    rotCanvas.height = bBoxHeight;

    // translate canvas context to a central point to allow rotation around the center
    rotCtx.translate(bBoxWidth / 2, bBoxHeight / 2);
    rotCtx.rotate(rotRad);
    rotCtx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    rotCtx.translate(-image.naturalWidth / 2, -image.naturalHeight / 2);

    // draw rotated image
    rotCtx.drawImage(image, 0, 0);

    // Get the cropped image data from the rotated canvas
    // We need to adjust pixelCrop.x and y to be relative to the rotated canvas's top-left corner
    const data = rotCtx.getImageData(
      pixelCrop.x + (bBoxWidth - image.naturalWidth) / 2,
      pixelCrop.y + (bBoxHeight - image.naturalHeight) / 2,
      pixelCrop.width,
      pixelCrop.height
    );

    // Create a new canvas to put the cropped data into
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    if (!finalCtx) return null;

    // set final canvas dimensions to the cropped image dimensions
    finalCanvas.width = pixelCrop.width;
    finalCanvas.height = pixelCrop.height;

    // put the cropped image data to the final canvas
    finalCtx.putImageData(data, 0, 0);

    console.log(`getCroppedImage: Canvas dimensions before toBlob: ${finalCanvas.width}x${finalCanvas.height}`);

    return new Promise((resolve) => {
      finalCanvas.toBlob((blob) => {
        if (blob) {
          const fileName = logoFile?.name || `team_logo_${Date.now()}.png`;
          const fileType = logoFile?.type || 'image/png';
          const croppedFile = new File([blob], fileName, { type: fileType });
          console.log("getCroppedImage: Generated blob and file", blob, croppedFile);
          resolve(croppedFile);
        } else {
          console.log("getCroppedImage: Blob generation failed");
          resolve(null);
        }
      }, logoFile?.type || 'image/png');
    });
  };

  const handleCropConfirm = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        console.log("handleCropConfirm: Starting crop process");
        const croppedFile = await getCroppedImage(imageSrc, croppedAreaPixels, rotation);
        if (croppedFile) {
          // Revoke the old URL to prevent memory leaks if it exists
          if (logoPreviewUrl) {
            URL.revokeObjectURL(logoPreviewUrl);
          }
          setLogoFile(croppedFile);
          const newPreviewUrl = URL.createObjectURL(croppedFile);
          setLogoPreviewUrl(newPreviewUrl);
          console.log("handleCropConfirm: Cropped file set, new preview URL:", newPreviewUrl);
        } else {
          console.log("handleCropConfirm: Cropped file is null");
        }
      } catch (error) {
        console.error('Error during crop confirmation:', error);
      }
    }
    setIsCropDialogOpen(false); // Close dialog
  };

  const handleCropCancel = () => {
    // Revoke the old URL on cancel to prevent memory leaks
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoFile(null);
    setImageSrc(null);
    setLogoPreviewUrl(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setIsCropDialogOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Use the logoFile state directly which is set after cropping confirmation
    try {
      await addTeam({
        name,
        shortCode,
        color,
        tournamentId,
        logoFile, // Pass the logo file (which is already cropped if dialog was used)
      });
      navigate('/teams');
    } catch (error) {
      console.error('Error creating team:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  function extractTeamId(input: string): string | null {
    if (!input) return null;
    try {
      const url = new URL(input);
      const segments = url.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || null;
    } catch {
      return input;
    }
  }

  function parseTeamInputs(input: string): string[] {
    return Array.from(
      new Set(
        input
          .split(/[\n,\s]+/)
          .map(s => s.trim())
          .filter(Boolean)
      )
    );
  }

  React.useEffect(() => {
    const ids = parseTeamInputs(existingTeamInput);
    if (ids.length === 0) {
      setPreviewTeams([]);
      setPreviewErrors({});
      return;
    }
    setPreviewLoading(true);
    setPreviewErrors({});
    Promise.all(
      ids.map(async (raw) => {
        const id = extractTeamId(raw);
        if (!id) return { id: raw, error: 'Invalid input' };
        try {
          const team = await getTeamById(id);
          return { ...team, input: raw };
        } catch {
          return { id: raw, error: 'Team not found', input: raw };
        }
      })
    ).then(results => {
      setPreviewTeams(results.filter(t => !t.error));
      const errors: Record<string, string> = {};
      results.forEach(t => { if (t.error) errors[t.input || t.id] = t.error; });
      setPreviewErrors(errors);
    }).finally(() => setPreviewLoading(false));
  }, [existingTeamInput]);

  const handleAddExistingTeam = (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingExisting(true);
    // TODO: Implement actual add logic for all previewTeams
    setTimeout(() => {
      toast({ title: 'Teams added (stub)', description: `${previewTeams.length} team(s) added.` });
      setIsAddingExisting(false);
      setExistingTeamInput('');
      setPreviewTeams([]);
      setPreviewErrors({});
    }, 1000);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <Button 
        variant="ghost" 
        onClick={() => navigate("/teams")} 
        className="mb-4"
      >
        ← Back to Teams
      </Button>
      
      <Tabs defaultValue="new" className="w-full">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl">Add New Team</CardTitle>
            <TabsList>
              <TabsTrigger value="new">Add New Team</TabsTrigger>
              <TabsTrigger value="existing">Add Existing Team</TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent>
            <TabsContent value="new">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Team Name <span className="text-red-500">*</span></Label>
                  <Input 
                    id="name" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter team name" 
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shortCode">Team Short Code (max 3 chars)</Label>
                  <Input 
                    id="shortCode" 
                    value={shortCode} 
                    onChange={(e) => setShortCode(e.target.value.slice(0, 3).toUpperCase())}
                    placeholder="E.g. IND, AUS, ENG" 
                    maxLength={3}
                  />
                </div>

                <div className="flex items-center justify-between w-full">
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="logo">Team Logo</Label>
                    <Input 
                      id="logo" 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange}
                      className="w-fit"
                      ref={fileInputRef}
                    />
                  </div>
                  {!logoPreviewUrl ? (
                    <div 
                      className="flex items-center justify-center w-24 h-24 rounded-full bg-gray-200 text-gray-500 cursor-pointer border border-gray-300"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L20 16m-2-6a2 2 0 100-4 2 2 0 000 4zM6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div 
                      className="relative flex items-center group"
                    >
                      <img 
                        key={logoPreviewUrl}
                        src={logoPreviewUrl} 
                        alt="Team Logo Preview" 
                        className="w-24 h-24 rounded-full object-cover border border-gray-200"
                        onError={(e) => console.error("Error loading cropped image preview:", e)}
                      />
                      <div 
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                        onClick={() => setIsCropDialogOpen(true)}
                      >
                        <Pencil className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  )}
                </div>

                {imageSrc && isCropDialogOpen && (
                  <Dialog open={isCropDialogOpen} onOpenChange={setIsCropDialogOpen}>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Crop Team Logo</DialogTitle>
                        <DialogDescription>
                          Adjust the crop area, zoom, and rotation for your team logo.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="relative w-full h-80 bg-gray-100 overflow-hidden flex items-center justify-center">
                        <Cropper
                          image={imageSrc}
                          crop={crop}
                          zoom={zoom}
                          rotation={rotation}
                          aspect={1} // For circular crop, aspect ratio should be 1:1
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onRotationChange={setRotation}
                          onCropComplete={onCropComplete}
                          cropShape="round" // Makes the crop area circular
                          showGrid={false}
                          restrictPosition={false}
                        />
                      </div>
                      <div className="space-y-4 py-4">
                        <div className="flex items-center gap-2">
                          <Label>Zoom:</Label>
                          <Slider
                            min={1}
                            max={3}
                            step={0.1}
                            value={[zoom]}
                            onValueChange={([val]) => setZoom(val)}
                            className="flex-grow"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label>Rotation:</Label>
                          <Slider
                            min={0}
                            max={360}
                            step={1}
                            value={[rotation]}
                            onValueChange={([val]) => setRotation(val)}
                            className="flex-grow"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={handleCropCancel}>
                          Cancel
                        </Button>
                        <Button onClick={handleCropConfirm}>OK</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                <div className="space-y-2">
                  <Label htmlFor="tournament">Tournament <span className="text-red-500">*</span></Label>
                  {isLoading ? (
                    <div className="py-2">Loading tournaments...</div>
                  ) : (
                    <Select 
                      onValueChange={setTournamentId} 
                      required
                    >
                      <SelectTrigger id="tournament">
                        <SelectValue placeholder="Select a tournament" />
                      </SelectTrigger>
                      <SelectContent>
                        {tournaments.map(tournament => (
                          <SelectItem key={tournament.id} value={tournament.id}>
                            {tournament.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Team Color</Label>
                  <ColorPicker value={color} onChange={setColor} />
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating Team...' : 'Add Team'}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="existing">
              <form onSubmit={handleAddExistingTeam} className="space-y-4">
                <Label htmlFor="existing-team-input">Team Profile URLs, Unique IDs, or Team IDs (one per line, comma, or space)</Label>
                <textarea
                  id="existing-team-input"
                  value={existingTeamInput}
                  onChange={e => setExistingTeamInput(e.target.value)}
                  placeholder="Paste team profile URLs, unique IDs, or team IDs (one per line, comma, or space)"
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  required
                />
                {previewLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin h-5 w-5" /> Loading...</div>
                )}
                {Object.keys(previewErrors).length > 0 && !previewLoading && (
                  <div className="text-destructive text-sm">Team(s) not found</div>
                )}
                {previewTeams.length > 0 && !previewLoading && (
                  <div className="space-y-2 mt-2">
                    {previewTeams.map(team => (
                      <div key={team.id} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                        <Avatar className="h-12 w-12">
                          {team.logoUrl ? (
                            <AvatarImage src={team.logoUrl} alt={team.name} />
                          ) : (
                            <AvatarFallback>{team.name?.[0] || '?'}</AvatarFallback>
                          )}
                        </Avatar>
                        <div>
                          <div className="font-semibold text-lg">{team.name}</div>
                          {team.shortCode && <div className="text-muted-foreground text-sm">{team.shortCode}</div>}
                          {team.color && <div className="w-4 h-4 rounded-full inline-block ml-2" style={{ backgroundColor: team.color }} title={team.color}></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button type="submit" disabled={isAddingExisting || previewTeams.length === 0}>
                  {isAddingExisting ? 'Adding...' : `Add ${previewTeams.length > 1 ? `${previewTeams.length} Teams` : 'Team'}`}
                </Button>
              </form>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default NewTeamPage;
